import { forwardRef, useState } from 'react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Button } from '@/components/ui/button';

import { RowInput } from '../MenuBuilder/RowInput';
import { FeaturePickerModal } from '../MenuBuilder/FeaturePickerModal';
import { LABEL_ADD_EXTERNAL_URL, LABEL_ADD_HEALTHERA_FEATURE } from '../MenuBuilder/labels';

export interface ImageLinkItem {
  assetId: string;
  thumbnailUrl: string;
  imageUrl: string;
  alt?: string;
  linkTitle?: string;
  linkHref?: string;
  isReadOnly?: boolean;
}

export type LinkFields = Pick<ImageLinkItem, 'linkTitle' | 'linkHref' | 'isReadOnly'>;

export interface ImageLinkRowProps {
  item: ImageLinkItem;
  onLinkChange: (fields: LinkFields) => void;
  onLinkClear: () => void;
  onDelete: () => void;
  linkHrefRequired?: boolean;
  error?: string;
  handle?: ReactNode;
  style?: CSSProperties;
}

export const ImageLinkRow = forwardRef<HTMLDivElement, ImageLinkRowProps>(function ImageLinkRow(
  { item, onLinkChange, onLinkClear, onDelete, linkHrefRequired = false, error, handle, style },
  ref
): ReactElement {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [featurePickerOpen, setFeaturePickerOpen] = useState(false);

  const hasLink = item.isReadOnly !== undefined;

  // Preserve any existing values when switching to external so that
  // internal → external carries over the title/href for the user to edit.
  // For a fresh "Add link" action both fields are undefined → ''.
  function handleExternalClick() {
    setMenuAnchor(null);
    onLinkChange({
      linkTitle: item.linkTitle ?? '',
      linkHref: item.linkHref ?? '',
      isReadOnly: false,
    });
  }

  function handleInternalClick() {
    setMenuAnchor(null);
    setFeaturePickerOpen(true);
  }

  let linkActionControl: ReactNode;
  if (hasLink) {
    linkActionControl = (
      <IconButton
        size="medium"
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        aria-label="Edit link"
      >
        <EditIcon fontSize="small" />
      </IconButton>
    );
  } else {
    linkActionControl = (
      <Button
        type="button"
        variant="outline"
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        className="vtwo:shrink-0 vtwo:normal-case vtwo:text-gray-500"
      >
        Add link
      </Button>
    );
  }

  return (
    <div
      ref={ref}
      style={style}
      className="vtwo:flex vtwo:flex-col vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white"
    >
      <div className="vtwo:flex vtwo:items-center vtwo:gap-2 vtwo:px-4 vtwo:py-2">
        {handle}
        <Box className="vtwo:h-16 vtwo:aspect-video vtwo:rounded-lg vtwo:overflow-hidden vtwo:shrink-0 vtwo:bg-gray-100 vtwo:mr-auto">
          {item.imageUrl && (
            <img
              src={item.imageUrl}
              alt={item.alt ?? ''}
              className="vtwo:w-full vtwo:h-full vtwo:object-cover"
            />
          )}
        </Box>
        {linkActionControl}
        <IconButton size="medium" onClick={onDelete} aria-label="Delete image">
          <DeleteIcon fontSize="small" />
        </IconButton>

        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={handleExternalClick}>{LABEL_ADD_EXTERNAL_URL}</MenuItem>
          <MenuItem onClick={handleInternalClick}>{LABEL_ADD_HEALTHERA_FEATURE}</MenuItem>
        </Menu>

        <FeaturePickerModal
          open={featurePickerOpen}
          onClose={() => setFeaturePickerOpen(false)}
          onSelect={(navItem) => {
            onLinkChange({ linkTitle: navItem.title, linkHref: navItem.href, isReadOnly: true });
            setFeaturePickerOpen(false);
          }}
        />
      </div>
      {item.isReadOnly === true && (
        <div className="vtwo:flex vtwo:items-center vtwo:gap-2 vtwo:px-4 vtwo:pb-3">
          <LinkIcon className="vtwo:text-gray-400 vtwo:text-[18px] vtwo:shrink-0" />
          <span className="vtwo:text-sm vtwo:text-gray-600 vtwo:truncate vtwo:min-w-0 vtwo:flex-1">
            {item.linkTitle || item.linkHref}
          </span>
          {!linkHrefRequired && (
            <IconButton size="small" onClick={onLinkClear} aria-label="Remove link">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      )}
      {item.isReadOnly === false && (
        <div className="vtwo:flex vtwo:flex-col vtwo:gap-2 vtwo:px-4 vtwo:pb-3">
          <RowInput
            value={item.linkTitle ?? ''}
            onChange={(v) =>
              onLinkChange({ linkTitle: v, linkHref: item.linkHref ?? '', isReadOnly: false })
            }
            placeholder="Link title"
          />
          <RowInput
            value={item.linkHref ?? ''}
            onChange={(v) =>
              onLinkChange({ linkTitle: item.linkTitle ?? '', linkHref: v, isReadOnly: false })
            }
            placeholder="https://"
          />
          {!linkHrefRequired && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onLinkClear}
              className="vtwo:self-start vtwo:text-gray-500"
            >
              Remove link
            </Button>
          )}
        </div>
      )}
      {error && (
        <p className="vtwo:text-xs vtwo:text-red-500 vtwo:px-4 vtwo:pb-2 vtwo:m-0">{error}</p>
      )}
    </div>
  );
});
